/**
 * Live streaming — start, hold and stop a meeting's RTMP broadcast.
 *
 * Calls the backend (POST /api/v1/meetings/:id/stream/*), which forwards to the
 * SFU orchestrator and on to the mixer holding the room. Mirrors the auth and
 * error handling of sipDial.api / recordings.api.
 *
 * The stream key is sent once, on start. Nothing here keeps it: not in the
 * store, not in localStorage, not in a retry buffer. If a stream has to be
 * restarted the host enters it again — which is the intended trade, because a
 * key kept anywhere in the browser is a credential that outlives the meeting.
 */

import { config } from '@/config/env'

const API = config.apiUrl

export class LiveStreamError extends Error {}

/**
 * The platforms the dialog can offer. Only youtube, facebook and custom have an
 * ingest behind them; twitch and linkedin are listed because the picker shows
 * them, and the backend answers with what to do instead. They are NOT quietly
 * folded into youtube — that published a Twitch key to YouTube's ingest.
 */
export type StreamPlatform = 'youtube' | 'facebook' | 'custom' | 'twitch' | 'linkedin'

export interface StartStreamInput {
  platform: StreamPlatform
  streamKey: string
  /** Only for `platform: 'custom'` — the ingest URL the key is appended to. */
  rtmpUrl?: string
}

export interface StreamState {
  streaming: boolean
  startedAt: number | null
  durationSeconds: number | null
  recording: boolean
  /** Why it stopped, when the platform refused or dropped the connection. */
  message?: string
}

function appToken(): string {
  try {
    const raw =
      localStorage.getItem('inmeet_auth_session') || sessionStorage.getItem('inmeet_auth_session')
    if (!raw) return ''
    const session = (JSON.parse(raw) as { session?: { accessToken?: string; expiresAt?: number } })
      ?.session
    if (session?.expiresAt && session.expiresAt <= Date.now()) return ''
    return session?.accessToken || ''
  } catch {
    return ''
  }
}

async function call<T>(
  meetingId: string,
  path: string,
  init?: { method?: 'GET' | 'POST'; body?: unknown; keepalive?: boolean },
): Promise<T> {
  const token = appToken()
  if (!token) throw new LiveStreamError('Not signed in')

  const res = await fetch(
    `${API}/api/v1/meetings/${encodeURIComponent(meetingId)}/stream${path}`,
    {
      method: init?.method ?? 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
      // `keepalive` lets a request survive the page unloading, which is the
      // only way the stop on refresh has a chance of arriving.
      keepalive: init?.keepalive,
    },
  )

  if (!res.ok) {
    let message = `Live stream request failed (${res.status})`
    try {
      const body = await res.json()
      message = body?.error?.message || body?.error?.code || message
    } catch {
      /* keep the status-based message */
    }
    throw new LiveStreamError(message)
  }

  return (await res.json()) as T;
}

/**
 * Go live. Resolves with how often the caller must heartbeat to stay live.
 *
 * The interval is the server's to decide — it derives from the lease the mixer
 * is holding, and a client guessing its own would either waste calls or let a
 * healthy stream lapse.
 */
export function startStream(
  meetingId: string,
  input: StartStreamInput,
): Promise<{ streaming: boolean; platform: string; startedAt: number; heartbeatSeconds: number }> {
  return call(meetingId, '/start', { body: input });
}

export interface HeartbeatResult {
  streaming: boolean
  /** Set when the stream ended for a reason worth showing the host. */
  message?: string
}

/**
 * Hold the lease open. Resolving with `streaming: false` is a normal answer —
 * the lease may have lapsed, someone else may have stopped it, or the platform
 * may have refused the key — and tells the caller to stop heartbeating.
 *
 * When the platform refused us, `message` says so. This is the only call in
 * flight at that moment, so it is the host's one chance to be told.
 */
export async function heartbeatStream(meetingId: string): Promise<HeartbeatResult> {
  const res = await call<{ streaming: boolean; message?: string }>(
    meetingId, '/heartbeat', { body: {} },
  );
  return { streaming: res.streaming === true, message: res.message };
}

export function stopStream(meetingId: string, reason = 'host_stopped'): Promise<{ streaming: boolean }> {
  return call(meetingId, '/stop', { body: { reason } });
}

/**
 * Best-effort stop while the page is unloading.
 *
 * Not awaited and never throws: at `pagehide` there is no time to handle a
 * failure and no UI left to report it to. If it does not arrive, the lease
 * expiring is the backstop — this only makes the common case immediate.
 */
export function stopStreamOnUnload(meetingId: string): void {
  try {
    void call(meetingId, '/stop', {
      body: { reason: 'page_unload' },
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* the lease will end it */
  }
}

export function getStreamState(meetingId: string): Promise<StreamState> {
  return call(meetingId, '', { method: 'GET' });
}
